import { customAlphabet } from 'nanoid';
import { alphanumeric } from 'nanoid-dictionary';

function randomId() {
  return Math.floor(Math.random() * 4000 + 1000);
}

function trimSlashes(uri: string) {
  uri = uri.replace(/^\/+/, ''); // Remove leading slashes
  uri = uri.replace(/\/+$/, ''); // Remove trailing slashes
  return uri;
}

function joinUrl(baseUrl: string, ...uris: string[]) {
  baseUrl = baseUrl.replace(/\/+$/, '');

  const joinedUris = uris.map(uri => trimSlashes(uri));

  const fullURL = [baseUrl, ...joinedUris].join('/').replace(/\/+$/, '');
  return fullURL;
}

function adjustProtocol(url: string) {
  if (window.location.protocol === 'https:') {
    return url.replace(/^http:/, 'https:');
  }
  return url;
}

function setWindowTitle(title: string) {
  document.title = title;
}

/**
 * 获取参数
 * @param name
 * @returns
 */
function getUrlKey(name: string): string | null {
  const url = new URL(location.href);
  const params = new URLSearchParams(url.search);
  const value = params.get(name);
  return value ? decodeURIComponent(value) : null;
}

/**
 * 延迟一段时间
 * @param ms 延迟时间（毫秒）
 * @returns 无
 */
function delay(ms: number): Promise<void> {
  return new Promise(function(resolve) {
    setTimeout(resolve, ms);
  });
}

/**
 * 判断是否为绝对路径URL
 * 1.带有协议的 URL（例如 http://example.com）
 * 2.协议相对的 URL（例如 //example.com）
 * 3.以域名开头的 URL（例如 www.example.com）
 * @param url
 */
function isAbsoluteURL(url: string) {
  const regex = /^(?:[a-zA-Z][a-zA-Z\d+\-.]*:)?\/\/|^[\w-]+\.[\w.-]+\//;
  return regex.test(url);
}

function isAliyunOSSUrl(url: string) {
  const regex = /^https?:\/\/([^.]+)\.oss-([^.]+)\.aliyuncs\.com\/(.+)$/;
  return regex.test(url);
}

function isStyle3dUrl(url: string) {
  const regex = /^https?:\/\/file\.style3d\.com(\/.*)?$/;
  return regex.test(url);
}

/**
 * 判断是否为 Base64 编码的图片数据 URL
 * @param url
 */
function isBase64Image(url: string) {
  const regex = /^data:image\/(png|jpeg|gif|bmp|webp);base64,[a-zA-Z0-9+/]+={0,2}$/;
  return regex.test(url);
}

function isWeixinBrowser() {
  const ua = navigator.userAgent.toLowerCase();

  const isWXWork = ua.match(/wxwork/i) !== null;

  const isWeixin = !isWXWork && ua.match(/MicroMessenger/i) !== null;

  return isWeixin;
}

function isIOS() {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
}

const nanoid = customAlphabet(alphanumeric, 10);

function generateRandomUUID(length: number = 10): string {
  return nanoid(length);
}

/**
 * 判断文件类型是否为图片或视频
 * @param filename 文件名或带后缀的文件路径
 * @returns 'image' | 'video' | 'unknown'
 */
function getFileType(filename: string): 'unknown' | 'image' | 'video' {
  if (!filename || typeof filename !== 'string') {
    return 'unknown';
  }

  const extension = filename.split('.').pop()?.toLowerCase() || '';

  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];

  if (imageExtensions.includes(extension)) {
    return 'image';
  } else if (videoExtensions.includes(extension)) {
    return 'video';
  }
  return 'unknown';
}

/**
 * 根据url判断资源是否为图片或视频
 * @param url 资源url
 * @returns 'image' | 'video' | 'unknown'
 */
function getFileTypeFromUrl(url: string): 'unknown' | 'image' | 'video' {
  const parsedUrl = new URL(url);
  const pathname = parsedUrl.pathname;
  const filename = pathname.substring(pathname.lastIndexOf('/') + 1);

  return getFileType(filename);
}

export {
  randomId,
  trimSlashes,
  joinUrl,
  adjustProtocol,
  setWindowTitle,
  getUrlKey,
  delay,
  isAbsoluteURL,
  isAliyunOSSUrl,
  isStyle3dUrl,
  isWeixinBrowser,
  isIOS,
  isBase64Image,
  generateRandomUUID,
  getFileType,
  getFileTypeFromUrl
};
